import { createHash } from "node:crypto";
import type { MemoryContract, RetrievalContract } from "./schemas.js";

export class DeterministicMemoryFake {
	private readonly records = new Map<string, MemoryContract>();

	put(memory: MemoryContract): void {
		this.records.set(memory.memoryId, structuredClone(memory));
	}

	get(memoryId: string): MemoryContract | undefined {
		const record = this.records.get(memoryId);
		return record ? structuredClone(record) : undefined;
	}

	retrieve(input: {
		requestId: string;
		ownerId: string;
		projectId: string;
		query: string;
		limit: number;
	}): RetrievalContract {
		const queryTerms = new Set(input.query.toLowerCase().split(/\s+/).filter(Boolean));
		const candidates = [...this.records.values()]
			.filter(
				(record) =>
					record.ownerId === input.ownerId && record.projectId === input.projectId && !record.tombstonedAt,
			)
			.map((record) => {
				const terms = record.content.toLowerCase().split(/\s+/).filter(Boolean);
				const matches = terms.filter((term) => queryTerms.has(term)).length;
				return { record, score: terms.length === 0 ? 0 : matches / terms.length };
			})
			.sort((left, right) => right.score - left.score || left.record.memoryId.localeCompare(right.record.memoryId))
			.slice(0, input.limit);
		return {
			header: { namespace: "ai.aletheion.asm", major: 1, minor: 0 },
			requestId: input.requestId,
			ownerId: input.ownerId,
			projectId: input.projectId,
			query: input.query,
			limit: input.limit,
			algorithm: "deterministic-lexical-v1",
			candidates: candidates.map(({ record, score }) => ({
				memoryId: record.memoryId,
				score,
				selected: score > 0,
				reasonCodes: score > 0 ? ["term_overlap"] : ["no_overlap"],
			})),
			startedAt: deterministicTimestamp(input.requestId),
			latencyMs: 0,
		};
	}
}

function deterministicTimestamp(seed: string): string {
	const seconds = Number.parseInt(createHash("sha256").update(seed).digest("hex").slice(0, 8), 16);
	return new Date(seconds * 1000).toISOString();
}
